import { useState, useEffect, useContext, useCallback, useRef } from "react";
import GeneralContext from "../../../utils/generalContext";
import { post } from "../../../Generals";
import Loader from "../../../Components/Loader";
import "./FlashcardsPlayer.css";

// =====================================================
// Text-to-Speech with natural voice
// =====================================================
const speak = (text, lang = "en-US") => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang;
    utterance.rate = 0.85;
    utterance.pitch = 1.0;
    // Try to use a natural Google voice
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find(
        (v) =>
            v.lang.startsWith(lang.split("-")[0]) &&
            (v.name.includes("Google") || v.name.includes("Natural") || v.name.includes("Samantha") || v.name.includes("Daniel"))
    );
    if (preferred) utterance.voice = preferred;
    else {
        const fallback = voices.find((v) => v.lang.startsWith(lang.split("-")[0]));
        if (fallback) utterance.voice = fallback;
    }
    window.speechSynthesis.speak(utterance);
};

// Preload voices
if (typeof window !== "undefined" && window.speechSynthesis) {
    window.speechSynthesis.onvoiceschanged = () => window.speechSynthesis.getVoices();
    window.speechSynthesis.getVoices();
}

// =====================================================
// Utility: Shuffle array
// =====================================================
const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

// =====================================================
// LANG map
// =====================================================
const langMap = { en: "en-US", es: "es-MX", fr: "fr-FR", it: "it-IT", de: "de-DE" };

// =====================================================
// MODE COMPONENTS
// =====================================================

/** MODE 1: Flip Card */
const FlipMode = ({ card, domain, onAnswer, lang }) => {
    const [flipped, setFlipped] = useState(false);

    useEffect(() => {
        setFlipped(false);
    }, [card.id]);

    const handleFlip = () => {
        if (!flipped) {
            setFlipped(true);
            speak(card.word, lang);
        }
    };

    return (
        <div className="fc-flip-container">
            <div className={`fc-flip-card ${flipped ? "fc-flipped" : ""}`} onClick={handleFlip}>
                <div className="fc-flip-front">
                    {card.img ? (
                        <img src={`${domain}/${card.img}`} alt={card.word} className="fc-card-img" />
                    ) : (
                        <div className="fc-card-placeholder">
                            <i className="fas fa-image fa-4x" style={{ opacity: 0.2 }}></i>
                        </div>
                    )}
                    <div className="fc-card-word">{card.translation}</div>
                    <div className="fc-tap-hint">Toca para voltear</div>
                </div>
                <div className="fc-flip-back">
                    <div className="fc-card-word-big">{card.word}</div>
                    {card.example_sentence && <div className="fc-example">"{card.example_sentence}"</div>}
                    <button className="fc-speak-btn" onClick={(e) => { e.stopPropagation(); speak(card.word, lang); }}>
                        <i className="fas fa-volume-up"></i>
                    </button>
                </div>
            </div>
            {flipped && (
                <div className="fc-answer-buttons">
                    <button className="fc-btn fc-btn-wrong" onClick={() => onAnswer(false)}>
                        <i className="fas fa-times mr-2"></i>No lo sabía
                    </button>
                    <button className="fc-btn fc-btn-correct" onClick={() => onAnswer(true)}>
                        <i className="fas fa-check mr-2"></i>¡Lo sé!
                    </button>
                </div>
            )}
        </div>
    );
};

/** MODE 2: Match Image — pick the correct image for a word */
const MatchImageMode = ({ card, allCards, domain, onAnswer, lang }) => {
    const [options, setOptions] = useState([]);
    const [selected, setSelected] = useState(null);
    const [answered, setAnswered] = useState(false);

    useEffect(() => {
        setSelected(null);
        setAnswered(false);
        // Pick 3 random wrong answers + the correct one
        const others = allCards.filter((c) => c.id !== card.id && c.img);
        const wrongOpts = shuffle(others).slice(0, 3);
        const opts = shuffle([card, ...wrongOpts]);
        setOptions(opts);
        speak(card.word, lang);
    }, [card.id]);

    const handleSelect = (opt) => {
        if (answered) return;
        setSelected(opt.id);
        setAnswered(true);
        const isCorrect = opt.id === card.id;
        setTimeout(() => onAnswer(isCorrect), 1200);
    };

    return (
        <div className="fc-match-container">
            <div className="fc-match-prompt">
                <span className="fc-match-label">¿Cuál imagen corresponde a</span>
                <span className="fc-match-word">{card.word}</span>
                <button className="fc-speak-btn-sm" onClick={() => speak(card.word, lang)}>
                    <i className="fas fa-volume-up"></i>
                </button>
            </div>
            <div className="fc-match-grid">
                {options.map((opt) => (
                    <div
                        key={opt.id}
                        className={`fc-match-option ${selected === opt.id ? (opt.id === card.id ? "fc-correct" : "fc-wrong") : ""} ${answered && opt.id === card.id ? "fc-correct" : ""}`}
                        onClick={() => handleSelect(opt)}
                    >
                        {opt.img ? (
                            <img src={`${domain}/${opt.img}`} alt="" className="fc-match-img" />
                        ) : (
                            <div className="fc-match-placeholder">{opt.translation}</div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

/** MODE 3: Type Answer — see image/translation, type the word */
const TypeMode = ({ card, domain, onAnswer, lang }) => {
    const [input, setInput] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [isCorrect, setIsCorrect] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        setInput("");
        setSubmitted(false);
        setIsCorrect(false);
        setTimeout(() => inputRef.current?.focus(), 300);
    }, [card.id]);

    const checkAnswer = () => {
        if (submitted || !input.trim()) return;
        const correct = input.trim().toLowerCase() === card.word.trim().toLowerCase();
        setIsCorrect(correct);
        setSubmitted(true);
        if (correct) speak(card.word, lang);
        setTimeout(() => onAnswer(correct), 1500);
    };

    return (
        <div className="fc-type-container">
            {card.img ? (
                <img src={`${domain}/${card.img}`} alt="" className="fc-type-img" />
            ) : (
                <div className="fc-type-placeholder-box">
                    <i className="fas fa-image fa-3x" style={{ opacity: 0.15 }}></i>
                </div>
            )}
            <div className="fc-type-translation">{card.translation}</div>
            {card.example_sentence && (
                <div className="fc-type-hint">
                    Pista: {card.example_sentence.replace(new RegExp(card.word, "gi"), "____")}
                </div>
            )}
            <div className="fc-type-input-row">
                <input
                    ref={inputRef}
                    type="text"
                    className={`fc-type-input ${submitted ? (isCorrect ? "fc-input-correct" : "fc-input-wrong") : ""}`}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && checkAnswer()}
                    placeholder="Escribe la palabra..."
                    disabled={submitted}
                    autoComplete="off"
                />
                {!submitted && (
                    <button className="fc-btn fc-btn-submit" onClick={checkAnswer} disabled={!input.trim()}>
                        <i className="fas fa-paper-plane"></i>
                    </button>
                )}
            </div>
            {submitted && !isCorrect && (
                <div className="fc-type-correct-answer">
                    La respuesta correcta es: <strong>{card.word}</strong>
                </div>
            )}
        </div>
    );
};

/** MODE 4: Listen & Choose — hear the word, pick the correct text */
const ListenMode = ({ card, allCards, onAnswer, lang }) => {
    const [options, setOptions] = useState([]);
    const [selected, setSelected] = useState(null);
    const [answered, setAnswered] = useState(false);

    useEffect(() => {
        setSelected(null);
        setAnswered(false);
        const others = allCards.filter((c) => c.id !== card.id);
        const wrongOpts = shuffle(others).slice(0, 3);
        const opts = shuffle([card, ...wrongOpts]);
        setOptions(opts);
        setTimeout(() => speak(card.word, lang), 500);
    }, [card.id]);

    const handleSelect = (opt) => {
        if (answered) return;
        setSelected(opt.id);
        setAnswered(true);
        const isCorrect = opt.id === card.id;
        setTimeout(() => onAnswer(isCorrect), 1200);
    };

    return (
        <div className="fc-listen-container">
            <div className="fc-listen-prompt">
                <button className="fc-listen-play" onClick={() => speak(card.word, lang)}>
                    <i className="fas fa-volume-up fa-2x"></i>
                </button>
                <span className="fc-listen-label">Escucha y selecciona la palabra correcta</span>
            </div>
            <div className="fc-listen-options">
                {options.map((opt) => (
                    <button
                        key={opt.id}
                        className={`fc-listen-option ${selected === opt.id ? (opt.id === card.id ? "fc-correct" : "fc-wrong") : ""} ${answered && opt.id === card.id ? "fc-correct" : ""}`}
                        onClick={() => handleSelect(opt)}
                    >
                        {opt.word}
                        <span className="fc-listen-translation">{opt.translation}</span>
                    </button>
                ))}
            </div>
        </div>
    );
};

// =====================================================
// SESSION RESULTS
// =====================================================
const SessionResults = ({ results, totalCards, onRestart, onFinish }) => {
    const correct = results.filter((r) => r.correct).length;
    const pct = Math.round((correct / results.length) * 100);
    const isPerfect = pct === 100;

    return (
        <div className="fc-results">
            {isPerfect && <div className="fc-confetti">🎉</div>}
            <div className="fc-results-circle">
                <svg viewBox="0 0 36 36" className="fc-circular-chart">
                    <path
                        className="fc-circle-bg"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <path
                        className="fc-circle-fill"
                        strokeDasharray={`${pct}, 100`}
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <text x="18" y="20.35" className="fc-circle-pct">{pct}%</text>
                </svg>
            </div>
            <h3 className="fc-results-title">
                {isPerfect ? "¡Perfecto! 🌟" : pct >= 70 ? "¡Buen trabajo! 👏" : "Sigue practicando 💪"}
            </h3>
            <div className="fc-results-stats">
                <div className="fc-stat">
                    <span className="fc-stat-num fc-green">{correct}</span>
                    <span className="fc-stat-label">Correctas</span>
                </div>
                <div className="fc-stat">
                    <span className="fc-stat-num fc-red">{results.length - correct}</span>
                    <span className="fc-stat-label">Incorrectas</span>
                </div>
                <div className="fc-stat">
                    <span className="fc-stat-num fc-blue">{results.length}</span>
                    <span className="fc-stat-label">Total</span>
                </div>
            </div>
            <div className="fc-results-actions">
                <button className="fc-btn fc-btn-restart" onClick={onRestart}>
                    <i className="fas fa-redo mr-2"></i>Practicar de nuevo
                </button>
                <button className="fc-btn fc-btn-finish" onClick={onFinish}>
                    <i className="fas fa-check-circle mr-2"></i>Terminar
                </button>
            </div>
        </div>
    );
};

// =====================================================
// MAIN FlashcardsPlayer COMPONENT
// =====================================================
const FlashcardsPlayer = (props) => {
    const { user, global } = useContext(GeneralContext);
    const { data } = props;
    const itemid = data?.moduloactual?.cursos_item?.id;
    const alumnoid = user?.user?.user?.id_alumno;

    const [loading, setLoading] = useState(true);
    const [deck, setDeck] = useState(null);
    const [cards, setCards] = useState([]);
    const [stats, setStats] = useState(null);
    const [sessionCards, setSessionCards] = useState([]);
    const [currentIdx, setCurrentIdx] = useState(0);
    const [mode, setMode] = useState(null); // null = select mode, 'flip', 'match', 'type', 'listen'
    const [results, setResults] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [sessionStarted, setSessionStarted] = useState(false);

    const MAX_SESSION_CARDS = 12;

    // Derive the TTS lang from deck data
    const lang = deck ? langMap[deck.idioma_destino] || "en-US" : "en-US";

    // Load deck data
    const loadDeck = useCallback(() => {
        if (!itemid || !alumnoid) return;
        setLoading(true);
        post(
            global.domain + "v3-api/v3/tinycardsGetDeck.json",
            { itemid, alumnoid },
            (resp) => {
                if (resp && resp.deck) {
                    setDeck(resp.deck);
                    setCards(resp.cards || []);
                    setStats(resp.stats || {});
                    // Session cards: take top N by priority
                    const session = (resp.cards || []).slice(0, MAX_SESSION_CARDS);
                    setSessionCards(session);
                }
                setLoading(false);
            }
        );
    }, [itemid, alumnoid, global.domain]);

    useEffect(() => {
        loadDeck();
    }, [loadDeck]);

    // Record progress
    const recordProgress = (cardid, deckid, correct) => {
        post(
            global.domain + "v3-api/v3/tinycardsRecordProgress.json",
            { alumnoid, cardid, deckid, correct: correct ? 1 : 0 },
            () => {} // fire-and-forget
        );
    };

    // Handle answer
    const handleAnswer = (correct) => {
        const card = sessionCards[currentIdx];
        recordProgress(card.id, deck.id, correct);
        setResults((prev) => [...prev, { cardId: card.id, correct, word: card.word }]);

        if (currentIdx + 1 >= sessionCards.length) {
            setShowResults(true);
        } else {
            setCurrentIdx((prev) => prev + 1);
        }
    };

    // Start a mode
    const startMode = (m) => {
        // For match and listen modes, need at least 4 cards
        if ((m === "match" || m === "listen") && cards.length < 4) {
            alert("Se necesitan al menos 4 tarjetas para este modo.");
            return;
        }
        setMode(m);
        setCurrentIdx(0);
        setResults([]);
        setShowResults(false);
        setSessionStarted(true);
    };

    // Restart
    const handleRestart = () => {
        loadDeck();
        setCurrentIdx(0);
        setResults([]);
        setShowResults(false);
    };

    // Finish & mark as done
    const handleFinish = () => {
        setMode(null);
        setSessionStarted(false);
        setShowResults(false);
        loadDeck(); // refresh stats
        // Mark the item as done
        if (props.markedAsDone && data?.moduloactual?.id) {
            props.markedAsDone(data.moduloactual.id);
        }
    };

    if (loading) return <Loader />;
    if (!deck) return <div className="alert alert-warning">No hay un deck de TinyCards configurado para este ítem.</div>;
    if (cards.length === 0) return <div className="alert alert-info">El deck "{deck.titulo}" no tiene tarjetas todavía.</div>;

    // Current card
    const currentCard = sessionCards[currentIdx];

    return (
        <div className="fc-player">
            {/* Header */}
            <div className="fc-header">
                <div className="fc-header-left">
                    <h4 className="fc-deck-title">
                        <i className="fas fa-clone mr-2"></i>{deck.titulo}
                    </h4>
                    {deck.descripcion && <p className="fc-deck-desc">{deck.descripcion}</p>}
                </div>
                <div className="fc-header-right">
                    <div className="fc-mini-stats">
                        <span className="fc-mini-stat" title="Dominadas"><i className="fas fa-star text-warning"></i> {stats?.mastered || 0}</span>
                        <span className="fc-mini-stat" title="Conocidas"><i className="fas fa-check text-success"></i> {stats?.known || 0}</span>
                        <span className="fc-mini-stat" title="Aprendiendo"><i className="fas fa-spinner text-info"></i> {stats?.learning || 0}</span>
                        <span className="fc-mini-stat" title="Nuevas"><i className="fas fa-plus text-secondary"></i> {stats?.new_cards || 0}</span>
                    </div>
                </div>
            </div>

            {/* Mode selector or active session */}
            {!sessionStarted ? (
                <div className="fc-mode-selector">
                    <h5 className="fc-mode-title">Elige un modo de práctica</h5>
                    <div className="fc-mode-grid">
                        <button className="fc-mode-card" onClick={() => startMode("flip")}>
                            <div className="fc-mode-icon">🔄</div>
                            <div className="fc-mode-name">Voltear</div>
                            <div className="fc-mode-desc">Ve la traducción y voltea para ver la palabra</div>
                        </button>
                        <button className="fc-mode-card" onClick={() => startMode("match")} disabled={cards.length < 4}>
                            <div className="fc-mode-icon">🖼️</div>
                            <div className="fc-mode-name">Imagen</div>
                            <div className="fc-mode-desc">Escucha la palabra y elige la imagen correcta</div>
                        </button>
                        <button className="fc-mode-card" onClick={() => startMode("type")}>
                            <div className="fc-mode-icon">✏️</div>
                            <div className="fc-mode-name">Escribir</div>
                            <div className="fc-mode-desc">Ve la imagen y escribe la palabra correcta</div>
                        </button>
                        <button className="fc-mode-card" onClick={() => startMode("listen")} disabled={cards.length < 4}>
                            <div className="fc-mode-icon">🔊</div>
                            <div className="fc-mode-name">Escuchar</div>
                            <div className="fc-mode-desc">Escucha y elige la palabra correcta</div>
                        </button>
                    </div>
                    <div className="fc-total-info">
                        <i className="fas fa-layer-group mr-1"></i>
                        {cards.length} tarjetas · Las más difíciles aparecen primero
                    </div>
                </div>
            ) : showResults ? (
                <SessionResults
                    results={results}
                    totalCards={sessionCards.length}
                    onRestart={handleRestart}
                    onFinish={handleFinish}
                />
            ) : (
                <div className="fc-session">
                    {/* Progress bar */}
                    <div className="fc-progress-bar">
                        <div className="fc-progress-fill" style={{ width: `${((currentIdx + 1) / sessionCards.length) * 100}%` }}></div>
                        <span className="fc-progress-text">{currentIdx + 1} / {sessionCards.length}</span>
                    </div>

                    {/* Active mode */}
                    <div className="fc-active-card">
                        {mode === "flip" && <FlipMode card={currentCard} domain={global.domain} onAnswer={handleAnswer} lang={lang} />}
                        {mode === "match" && <MatchImageMode card={currentCard} allCards={cards} domain={global.domain} onAnswer={handleAnswer} lang={lang} />}
                        {mode === "type" && <TypeMode card={currentCard} domain={global.domain} onAnswer={handleAnswer} lang={lang} />}
                        {mode === "listen" && <ListenMode card={currentCard} allCards={cards} onAnswer={handleAnswer} lang={lang} />}
                    </div>

                    {/* Back button */}
                    <button className="fc-back-btn" onClick={() => { setMode(null); setSessionStarted(false); }}>
                        <i className="fas fa-arrow-left mr-1"></i> Cambiar modo
                    </button>
                </div>
            )}
        </div>
    );
};

export default FlashcardsPlayer;
